import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Send, UploadCloud, MessageSquare, CheckCircle, Image as ImageIcon, Phone, RefreshCw, AlertCircle, Info, Sparkles, Users, Crown } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { API_BASE_URL } from "../config";

export default function DailyContent() {
    const [content, setContent] = useState("");
    const [mediaUrl, setMediaUrl] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [sendingBulk, setSendingBulk] = useState(false);
    const [testPhone, setTestPhone] = useState("");
    const [lastUpdated, setLastUpdated] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [targetAudience, setTargetAudience] = useState("0"); // "0": Premium, "1": Free, "all": All Users

    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/api/daily-content`);
            if (res.data.status && res.data.data) {
                setContent(res.data.data.content || "");
                setMediaUrl(res.data.data.media_url || "");
                setPreviewUrl(res.data.data.media_url || "");
                if (res.data.data.target_audience !== undefined && res.data.data.target_audience !== null) {
                    setTargetAudience(String(res.data.data.target_audience));
                }
                if (res.data.data.updated_at) {
                    setLastUpdated(new Date(res.data.data.updated_at).toLocaleString());
                }
            }
        } catch (err) {
            console.error("Fetch daily content error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 20 * 1024 * 1024) {
            Swal.fire("Warning", "Image size should be under 20MB", "warning");
            return;
        }

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleSaveContent = async () => {
        if (!content.trim()) {
            Swal.fire("Validation Error", "Daily Message Content (body_1) cannot be empty!", "error");
            return;
        }
        if (!previewUrl && !mediaUrl && !selectedFile) {
            Swal.fire("Validation Error", "Please provide a Header Image / Media URL for template header_1!", "error");
            return;
        }

        setLoading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append("content", content);
        formData.append("target_audience", targetAudience);
        if (selectedFile) {
            formData.append("image", selectedFile);
        } else if (mediaUrl) {
            formData.append("media_url", mediaUrl);
        }

        try {
            const res = await axios.post(`${API_BASE_URL}/api/daily-content/update`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(pct);
                    }
                }
            });

            if (res.data && res.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Saved Successfully!",
                    text: "Daily content & header media updated. It will be sent tomorrow morning at 8:50 AM automatically."
                });
                setSelectedFile(null);
                fetchContent();
            } else {
                throw new Error(res.data?.message || "Failed to save content");
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.response?.data?.message || err.message || "Failed to update daily content", "error");
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleSelectAudience = async (newAudience) => {
        setTargetAudience(newAudience);
        try {
            await axios.post(`${API_BASE_URL}/api/daily-content/set-audience`, {
                target_audience: newAudience
            });
            const audienceLabels = {
                "0": "0: Premium Users (is_verified = 0)",
                "1": "1: Free Users (is_verified = 1)",
                "all": "All Users"
            };
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true
            });
            Toast.fire({
                icon: 'success',
                title: `Morning 8:50 AM Cron Target set to: ${audienceLabels[newAudience] || newAudience}`
            });
        } catch (err) {
            console.error("Failed to update target audience:", err);
        }
    };

    const handleSendTest = async () => {
        const cleanPhone = testPhone.replace(/[^0-9]/g, '');
        if (!cleanPhone || cleanPhone.length < 10) {
            Swal.fire("Invalid Phone", "Please enter a valid 10-digit mobile number for test send.", "warning");
            return;
        }

        const activeMedia = mediaUrl || previewUrl;
        if (!activeMedia) {
            Swal.fire("Missing Media", "Please upload or provide an image URL for the WhatsApp header.", "warning");
            return;
        }
        if (!content.trim()) {
            Swal.fire("Missing Content", "Please enter message text before sending.", "warning");
            return;
        }

        setSendingTest(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/daily-content/send-whatsapp`, {
                test_phone: cleanPhone,
                content: content.trim(),
                media_url: activeMedia
            });

            if (res.data && res.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Test Sent!",
                    text: `WhatsApp message triggered to +${cleanPhone.length === 10 ? '91' + cleanPhone : cleanPhone}. Check your WhatsApp in a few seconds!`,
                });
            } else {
                throw new Error(res.data?.message || "Failed to send test message");
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Send Failed", err.response?.data?.message || err.message || "Failed to send test WhatsApp message", "error");
        } finally {
            setSendingTest(false);
        }
    };

    const handleSendBulkNow = async () => {
        const activeMedia = mediaUrl || previewUrl;
        if (!activeMedia) {
            Swal.fire("Missing Media", "Please upload or provide an image URL for the WhatsApp header.", "warning");
            return;
        }
        if (!content.trim()) {
            Swal.fire("Missing Content", "Please enter message text before sending.", "warning");
            return;
        }

        const audienceConfig = {
            "0": { title: "Premium Users (0)", filterText: "is_verified = 0 (Premium Only)", color: "text-amber-600" },
            "1": { title: "Free Users (1)", filterText: "is_verified = 1 (Free Only)", color: "text-emerald-600" },
            "all": { title: "All Users", filterText: "All registered users (Free + Premium)", color: "text-blue-600" }
        };
        const currentTargetInfo = audienceConfig[targetAudience] || audienceConfig["0"];

        const confirm = await Swal.fire({
            title: `Broadcast to ${currentTargetInfo.title}?`,
            html: `
                <div class="text-left text-sm text-slate-600">
                    <p class="mb-2">This will immediately send WhatsApp messages to <strong>${currentTargetInfo.filterText}</strong> using MSG91 template <code>sb_whatsap_daily</code>.</p>
                    <p class="p-2.5 bg-blue-50 text-blue-700 rounded-lg font-medium text-xs">
                        Integrated Number: <strong>919111191139</strong><br/>
                        Template: <strong>sb_whatsap_daily</strong><br/>
                        Selected Target: <strong>${currentTargetInfo.title}</strong>
                    </p>
                </div>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Yes, Send WhatsApp Now!"
        });

        if (!confirm.isConfirmed) return;

        setSendingBulk(true);
        Swal.fire({
            title: "Sending WhatsApp Broadcast...",
            text: `Please wait while messages are dispatched to ${currentTargetInfo.title} via MSG91...`,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            const res = await axios.post(`${API_BASE_URL}/api/daily-content/send-whatsapp`, {
                content: content.trim(),
                media_url: activeMedia,
                target_audience: targetAudience
            });

            Swal.close();
            if (res.data && res.data.status) {
                Swal.fire({
                    icon: "success",
                    title: "Broadcast Dispatched!",
                    html: `
                        <p>Successfully triggered WhatsApp messages to <strong>${res.data.totalRecipients || 'selected'}</strong> users!</p>
                        <p class="text-xs text-slate-500 mt-2">Target: ${currentTargetInfo.title} &bull; Batches: ${res.data.batchResults?.length || 1}</p>
                    `
                });
            } else {
                throw new Error(res.data?.message || "Failed to trigger broadcast");
            }
        } catch (err) {
            Swal.close();
            console.error(err);
            Swal.fire("Error", err.response?.data?.message || err.message || "Failed to send WhatsApp broadcast", "error");
        } finally {
            setSendingBulk(false);
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-slate-900 font-sans">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:pl-64">
                <Header
                    onMenuClick={() => setSidebarOpen(true)}
                    title="Daily WhatsApp Content"
                    subtitle="Manage daily broadcast message, media header, and MSG91 WhatsApp integration"
                />

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 pb-24">
                    <div className="max-w-5xl mx-auto space-y-6">

                        {/* Top Info Banner */}
                        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 rounded-2xl p-5 text-white shadow-md relative overflow-hidden">
                            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="p-1.5 bg-white/20 backdrop-blur-sm rounded-lg">
                                            <MessageSquare size={18} />
                                        </span>
                                        <h2 className="text-lg font-bold">MSG91 WhatsApp Broadcast (Daily)</h2>
                                    </div>
                                    <p className="text-xs text-blue-100 max-w-xl">
                                        Template: <code className="bg-white/20 px-1.5 py-0.5 rounded font-mono">sb_whatsap_daily</code> &bull; Sender: <code className="bg-white/20 px-1.5 py-0.5 rounded font-mono">919111191139</code> &bull; Morning 8:50 AM Cron Target: <strong className="text-white underline">{targetAudience === "0" ? "👑 0: Premium" : targetAudience === "1" ? "🆓 1: Free" : "👥 All Users"}</strong>
                                    </p>
                                </div>
                                {lastUpdated && (
                                    <div className="text-right text-[11px] text-blue-100 bg-white/10 px-3 py-1.5 rounded-xl backdrop-blur-sm shrink-0">
                                        <span>Last Updated:</span>
                                        <div className="font-semibold text-white">{lastUpdated}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* Left Column: Editor (2 cols) */}
                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm space-y-5">
                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3">
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                            <Sparkles size={18} className="text-blue-500" />
                                            <span>Content Details</span>
                                        </h3>
                                        <span className="text-xs text-slate-500">Auto-sent daily at 8:50 AM</span>
                                    </div>

                                    {/* Media Header (header_1) */}
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                                            1. Header Media Image (header_1) *
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                                            <label className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-200 transition-colors border border-dashed border-slate-300 dark:border-slate-600">
                                                <UploadCloud size={18} className="text-blue-500" />
                                                <span>{selectedFile ? selectedFile.name : "Choose Image File"}</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleFileSelect}
                                                />
                                            </label>
                                            <span className="text-xs text-slate-400">or enter media URL directly:</span>
                                        </div>

                                        <input
                                            type="url"
                                            className="mt-2 w-full p-2.5 text-xs font-mono border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="https://social-baazar.sgp1.digitaloceanspaces.com/..."
                                            value={mediaUrl}
                                            onChange={(e) => {
                                                setMediaUrl(e.target.value);
                                                setPreviewUrl(e.target.value);
                                            }}
                                        />
                                    </div>

                                    {/* Message Text (body_1) */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                2. Message Text Body (body_1) *
                                            </label>
                                            <span className="text-xs text-slate-400 font-mono">{content.length} characters</span>
                                        </div>
                                        <textarea
                                            rows={6}
                                            className="w-full p-3.5 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm dark:bg-slate-900 dark:text-slate-100 resize-y"
                                            placeholder="Enter your daily message body here. This will be filled in the WhatsApp template body_1 variable..."
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                        />
                                    </div>

                                    {/* Save Button */}
                                    <div className="pt-2 flex items-center justify-between">
                                        <div className="text-xs text-slate-500 flex items-center gap-1.5">
                                            <CheckCircle size={14} className="text-emerald-500" />
                                            <span>Changes are saved immediately to cloud</span>
                                        </div>
                                        <button
                                            onClick={handleSaveContent}
                                            disabled={loading}
                                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Saving...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle size={16} />
                                                    <span>Save Daily Content</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Send Actions Box */}
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
                                    <h3 className="font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2 border-b border-slate-100 dark:border-slate-700 pb-3">
                                        <Send size={18} className="text-emerald-500" />
                                        <span>Dispatch WhatsApp Message</span>
                                    </h3>

                                    {/* Test Single Number */}
                                    <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700/60">
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                                            Send Test Message to your Mobile
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-2.5">
                                            <div className="relative flex-1">
                                                <Phone size={16} className="absolute left-3 top-3 text-slate-400" />
                                                <input
                                                    type="tel"
                                                    placeholder="e.g. 918462099536 or 10-digit mobile"
                                                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                                    value={testPhone}
                                                    onChange={(e) => setTestPhone(e.target.value)}
                                                />
                                            </div>
                                            <button
                                                onClick={handleSendTest}
                                                disabled={sendingTest}
                                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium text-xs sm:text-sm transition-all shadow-sm hover:shadow disabled:opacity-50 flex items-center justify-center gap-1.5 shrink-0"
                                            >
                                                {sendingTest ? (
                                                    <>
                                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Sending Test...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send size={14} />
                                                        <span>Send Test Now</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Target Audience Selector */}
                                    <div className="space-y-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                                                <Users size={14} className="text-blue-500" />
                                                <span>Select Target Audience (Morning 8:50 AM Cron & Instant Send):</span>
                                            </label>
                                            <span className="text-[11px] text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded-full">
                                                Active for 8:50 AM Cron
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                            {/* 0 = Premium */}
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAudience("0")}
                                                className={`p-3 rounded-xl border text-left transition-all relative flex flex-col justify-between ${targetAudience === "0"
                                                    ? "bg-amber-500/10 border-amber-500 ring-2 ring-amber-500/20 shadow-sm"
                                                    : "bg-slate-50/70 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between w-full mb-1.5">
                                                    <span className="font-bold text-xs flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                                                        <Crown size={15} />
                                                        <span>0: Premium Users</span>
                                                    </span>
                                                    <input
                                                        type="radio"
                                                        name="targetAudience"
                                                        checked={targetAudience === "0"}
                                                        onChange={() => handleSelectAudience("0")}
                                                        className="accent-amber-600 cursor-pointer"
                                                    />
                                                </div>
                                                <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                                    Only users with <code>is_verified = 0</code>
                                                </div>
                                            </button>

                                            {/* 1 = Free */}
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAudience("1")}
                                                className={`p-3 rounded-xl border text-left transition-all relative flex flex-col justify-between ${targetAudience === "1"
                                                    ? "bg-emerald-500/10 border-emerald-500 ring-2 ring-emerald-500/20 shadow-sm"
                                                    : "bg-slate-50/70 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between w-full mb-1.5">
                                                    <span className="font-bold text-xs flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                                                        <CheckCircle size={15} />
                                                        <span>1: Free Users</span>
                                                    </span>
                                                    <input
                                                        type="radio"
                                                        name="targetAudience"
                                                        checked={targetAudience === "1"}
                                                        onChange={() => handleSelectAudience("1")}
                                                        className="accent-emerald-600 cursor-pointer"
                                                    />
                                                </div>
                                                <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                                    Only users with <code>is_verified = 1</code>
                                                </div>
                                            </button>

                                            {/* All Users */}
                                            <button
                                                type="button"
                                                onClick={() => handleSelectAudience("all")}
                                                className={`p-3 rounded-xl border text-left transition-all relative flex flex-col justify-between ${targetAudience === "all"
                                                    ? "bg-blue-500/10 border-blue-500 ring-2 ring-blue-500/20 shadow-sm"
                                                    : "bg-slate-50/70 dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between w-full mb-1.5">
                                                    <span className="font-bold text-xs flex items-center gap-1.5 text-blue-700 dark:text-blue-400">
                                                        <Users size={15} />
                                                        <span>All Users</span>
                                                    </span>
                                                    <input
                                                        type="radio"
                                                        name="targetAudience"
                                                        checked={targetAudience === "all"}
                                                        onChange={() => handleSelectAudience("all")}
                                                        className="accent-blue-600 cursor-pointer"
                                                    />
                                                </div>
                                                <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                                                    All registered users (0 & 1)
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Bulk Broadcast */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-blue-50/70 dark:bg-blue-950/20 rounded-xl border border-blue-100 dark:border-blue-900/40 gap-3">
                                        <div>
                                            <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                                <span>Broadcast WhatsApp Now</span>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${targetAudience === "0"
                                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-900"
                                                    : targetAudience === "1"
                                                        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900"
                                                        : "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-200 dark:border-blue-900"
                                                    }`}>
                                                    Target: {targetAudience === "0" ? "0: Premium" : targetAudience === "1" ? "1: Free" : "All Users"}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                {targetAudience === "0" && "Triggers outbound WhatsApp messages strictly to Premium users (is_verified = 0)."}
                                                {targetAudience === "1" && "Triggers outbound WhatsApp messages strictly to Free users (is_verified = 1)."}
                                                {targetAudience === "all" && "Triggers outbound WhatsApp messages to ALL registered users."}
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleSendBulkNow}
                                            disabled={sendingBulk}
                                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-all shadow-sm hover:shadow-md disabled:opacity-50 flex items-center gap-2 shrink-0"
                                        >
                                            {sendingBulk ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Broadcasting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles size={16} />
                                                    <span>
                                                        {targetAudience === "0" && "Broadcast to Premium (0)"}
                                                        {targetAudience === "1" && "Broadcast to Free (1)"}
                                                        {targetAudience === "all" && "Broadcast to All Users"}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Live WhatsApp Message Preview (1 col) */}
                            <div className="space-y-4">
                                <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3 mb-4">
                                        <h3 className="font-bold text-slate-800 dark:text-slate-100 text-sm flex items-center gap-2">
                                            <MessageSquare size={16} className="text-emerald-500" />
                                            <span>WhatsApp Message Preview</span>
                                        </h3>
                                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full">LIVE PREVIEW</span>
                                    </div>

                                    {/* Mock WhatsApp Chat Bubble */}
                                    <div className="bg-[#efeae2] dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-2">
                                        <div className="max-w-[280px] bg-white dark:bg-slate-800 rounded-xl rounded-tl-sm p-2 shadow-sm border border-slate-100 dark:border-slate-700/80 space-y-2">
                                            {/* Header Image */}
                                            {previewUrl ? (
                                                <div className="aspect-video w-full bg-slate-100 dark:bg-slate-900 rounded-lg overflow-hidden border border-slate-100 dark:border-slate-700 relative">
                                                    <img
                                                        src={previewUrl}
                                                        alt="header preview"
                                                        className="w-full h-full object-cover"
                                                        onError={() => setPreviewUrl("")}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="aspect-video w-full bg-slate-100 dark:bg-slate-900 rounded-lg flex flex-col items-center justify-center text-slate-400 text-xs border border-dashed border-slate-200 dark:border-slate-700">
                                                    <ImageIcon size={24} className="mb-1 opacity-50" />
                                                    <span>No Media Image</span>
                                                </div>
                                            )}

                                            {/* Body Text */}
                                            <div className="text-xs text-slate-800 dark:text-slate-100 whitespace-pre-wrap leading-relaxed">
                                                {content ? content : <span className="text-slate-400 italic">Enter message content to preview how it looks in WhatsApp...</span>}
                                            </div>

                                            <div className="text-[10px] text-slate-400 text-right">
                                                8:50 AM &bull; ✓✓
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 text-xs text-slate-500 dark:text-slate-400 space-y-1.5 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                        <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                                            <Info size={14} className="text-blue-500" />
                                            <span>Template Info</span>
                                        </div>
                                        <div className="text-[11px] font-mono text-slate-600 dark:text-slate-400">
                                            Name: sb_whatsap_daily<br />
                                            Language: en (deterministic)<br />
                                            Header: header_1 (image)<br />
                                            Body: body_1 (text)
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
